import SearchIcon from '@mui/icons-material/Search'
import { Box, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { useEntitiesFromSearch } from 'api/search'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import React, { useMemo, useState } from 'react'

const isDealActive = (deal: any, now = new Date()) => {
  const start = deal.startDate ? new Date(deal.startDate) : undefined
  const end = deal.endDate ? new Date(deal.endDate) : undefined
  if (start && isNaN(start.getTime())) return true
  if (end && isNaN(end.getTime())) return true
  if (start && now < start) return false
  if (end && now > end) return false
  return true
}

const DealReferenceContent: React.FC = () => {
  const theme = useTheme()
  const { deals = [] } = useEntitiesFromSearch()
  const [search, setSearch] = useState('')

  const activeDeals = useMemo(() => {
    const now = new Date()
    const term = search.toLowerCase().trim()
    return deals
      .filter(d => d?.EntityType === 'deal')
      .filter(d => isDealActive(d, now))
      .filter(d => {
        if (!term) return true
        return d?.name?.toLowerCase().includes(term) || d?.stage?.toLowerCase().includes(term)
      })
      .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
  }, [deals, search])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search active deals..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.8rem',
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 1,
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: theme.palette.background.scrollbar_thumb,
            borderRadius: 3,
          },
        }}
      >
        {activeDeals.length === 0 && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 4 }}>
            No active deals found
          </Typography>
        )}
        {activeDeals.map((deal, i) => (
          <Box
            key={deal.SK || i}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 1,
              px: 0.5,
              py: 0.4,
              borderRadius: 1,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            <Typography noWrap variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500, textAlign: 'left' }}>
              {deal.name || '--'}
            </Typography>
            <Typography
              noWrap
              variant="body2"
              sx={{
                fontSize: '0.72rem',
                color: deal.stage ? renderColorByString(deal.stage) : 'text.secondary',
                textAlign: 'right',
                maxWidth: 90,
              }}
            >
              {deal.stage || '--'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default DealReferenceContent
